import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="file-upload"
export default class extends Controller {
  static targets = ["input", "dropzone", "fileList", "error"]
  static values = {
    maxSize: { type: Number, default: 10485760 }, // 10MB default
    accept: { type: String, default: "" },
    multiple: { type: Boolean, default: true },
    nativeForm: { type: Boolean, default: false },
    preloadedActivities: { type: Array, default: [] },
    preloadedFiles: { type: Array, default: [] },
    deleteFileUrl: { type: String, default: "" },
    readOnly: { type: Boolean, default: false }
  }

  connect() {
    this.files = []
    this.persistedFiles = []
    this.activityChips = []

    // Listen for activity deselected events from activity-selector
    this.boundHandleActivityDeselected = this.handleActivityDeselected.bind(this)
    window.addEventListener('activity-selector:activityDeselected', this.boundHandleActivityDeselected)

    // Load preloaded activities
    this.loadPreloadedActivities()

    // Load preloaded files
    this.loadPreloadedFiles()
  }

  disconnect() {
    window.removeEventListener('activity-selector:activityDeselected', this.boundHandleActivityDeselected)
  }

  // Trigger file input when upload button is clicked
  triggerUpload(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  // Handle activity selected from activity selector
  handleActivitySelected(event) {
    const activity = event.detail.activity
    this.addActivityChip(activity)
  }

  // Handle activity deselected from activity selector
  handleActivityDeselected(event) {
    const activityId = event.detail.activityId
    const metadata = event.detail.metadata
    this.removeActivityChip(activityId, metadata)
  }

  // Add activity chip to the list
  addActivityChip(activity) {
    // Check if already added
    if (this.activityChips.find(chip => chip.id === activity.id)) {
      return
    }

    this.activityChips.push(activity)
    this.renderActivityChip(activity)
  }

  // Remove activity chip from the list
  removeActivityChip(activityId, metadata = null) {
    // Try to find chip by ID first
    let chipToRemove = this.activityChips.find(chip => chip.id === activityId)
    let chipElement = this.fileListTarget.querySelector(`[data-activity-chip][data-activity-id="${activityId}"]`)

    // If not found by ID and we have metadata, try to find by title+phase
    if (!chipToRemove && metadata) {
      chipToRemove = this.activityChips.find(chip =>
        chip.title === metadata.title &&
        (chip.phaseNumber || '') === (metadata.phaseNumber || '')
      )

      if (chipToRemove) {
        // Find the DOM element with the chip's actual ID
        chipElement = this.fileListTarget.querySelector(`[data-activity-chip][data-activity-id="${chipToRemove.id}"]`)
      }
    }

    // Remove from array
    if (chipToRemove) {
      this.activityChips = this.activityChips.filter(chip =>
        !(chip.title === chipToRemove.title &&
          (chip.phaseNumber || '') === (chipToRemove.phaseNumber || ''))
      )
    }

    // Remove from DOM
    if (chipElement) {
      chipElement.remove()
    }
  }

  // Render a single activity chip
  renderActivityChip(activity) {
    // Format display based on whether it's single or multiple phases
    // Check for null, undefined, and empty string
    const hasPhase = activity.phaseNumber !== null &&
                     activity.phaseNumber !== undefined &&
                     activity.phaseNumber !== ''
    const activityNumber = activity.activityNumber || ''

    // For multiple phases: show phase badge + activity number and name
    // For single phase: show activity number and name only
    const displayText = activityNumber
      ? `${activityNumber}. ${this.escapeHtml(activity.title)}`
      : this.escapeHtml(activity.title)

    const chipHtml = `
      <div class="inline-flex items-center gap-2 px-4 py-2 bg-[#F3F0FF] border border-[#DDD6FE] rounded-lg" data-activity-chip data-activity-id="${activity.id}">
        ${hasPhase ? `
          <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#E4E5E8] text-[#1F2937] border-0">
            Phase ${activity.phaseNumber}
          </span>
        ` : ''}
        <span class="font-semibold text-sm leading-5 text-[#1F2937]">${displayText}</span>
        ${!this.readOnlyValue ? `
          <button type="button"
                  class="flex items-center justify-center hover:opacity-70 transition-opacity"
                  data-action="click->file-upload#removeActivity"
                  data-activity-id="${activity.id}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        ` : ''}
      </div>
    `
    this.fileListTarget.insertAdjacentHTML('beforeend', chipHtml)
  }

  // Remove activity chip (called when user clicks X button)
  removeActivity(event) {
    event.preventDefault()
    const activityId = event.currentTarget.dataset.activityId

    // Remove the chip
    this.removeActivityChip(activityId)

    // Dispatch custom event on window so activity-selector can receive it
    const customEvent = new CustomEvent('file-upload:activityRemoved', {
      detail: { activityId: activityId },
      bubbles: true,
      cancelable: true
    })
    window.dispatchEvent(customEvent)
  }

  // Handle file selection from input
  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    this.addFiles(files)
    // Reset input so same file can be selected again
    if (!this.nativeFormValue) event.target.value = ''
  }

  // Handle drag over
  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('bg-purple-50', 'border-purple-300')
    }
  }

  // Handle drag leave
  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('bg-purple-50', 'border-purple-300')
    }
  }

  // Handle file drop
  drop(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('bg-purple-50', 'border-purple-300')
    }

    const files = Array.from(event.dataTransfer.files)
    this.addFiles(files)
  }

  // Add files to the list
  addFiles(newFiles) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
    // Filter out files that are too large
    const validFiles = newFiles.filter(file => {
      if (this.nativeFormValue && this.acceptValue && !this.acceptValue.split(",").includes(file.type)) {
        this.showError("Choose a PNG, JPEG, or WebP image.")
        return false
      }
      if (file.size > this.maxSizeValue) {
        this.showError(`${file.name} is too large. Maximum size is ${this.formatBytes(this.maxSizeValue)}`)
        return false
      }
      return true
    })

    // Add to files array
    if (this.multipleValue) {
      this.files = [...this.files, ...validFiles]
    } else {
      this.files = validFiles.slice(0, 1)
    }

    this.syncNativeInput()
    this.renderFiles()
    this.dispatch("filesAdded", { detail: { files: this.files } })
  }

  // Remove a file from the list
  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)
    this.files.splice(index, 1)
    this.syncNativeInput()
    this.renderFiles()
    this.dispatch("fileRemoved", { detail: { files: this.files } })
  }

  // Native Rails forms submit the input itself instead of calling getFiles().
  syncNativeInput() {
    if (!this.nativeFormValue) return

    const transfer = new DataTransfer()
    this.files.forEach(file => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  // Render the file list (pending uploads - purple chips)
  renderFiles() {
    if (!this.hasFileListTarget) return

    // Remove existing file chips (keep activity chips and persisted file chips)
    const existingFileChips = this.fileListTarget.querySelectorAll('[data-file-chip]')
    existingFileChips.forEach(chip => chip.remove())

    if (this.files.length === 0) {
      return
    }

    // Append file chips
    this.files.forEach((file, index) => {
      const chipHtml = `
        <div class="inline-flex max-w-full min-w-0 items-center gap-2 px-3 py-2 bg-[#F3F0FF] border border-[#DDD6FE] rounded-lg" data-file-chip>
          <span class="w-4 h-4 inline-flex items-center justify-center text-brand-600">
            <svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14 11H8M10 15H8M16 7H8M20 6.8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H15.2C16.8802 2 17.7202 2 18.362 2.32698C18.9265 2.6146 19.3854 3.07354 19.673 3.63803C20 4.27976 20 5.11984 20 6.8Z"/>
            </svg>
          </span>
          <span class="min-w-0 flex-1 truncate font-normal text-sm leading-5 text-[#1F2937]" title="${this.escapeHtml(file.name)}">${this.escapeHtml(file.name)}</span>
          <span class="font-normal text-xs leading-5 text-[#6B7280]">(${this.formatBytes(file.size)})</span>
          ${!this.readOnlyValue ? `
            <button type="button"
                    class="flex items-center justify-center hover:opacity-70 transition-opacity text-gray-500"
                    aria-label="Remove selected file"
                    data-action="click->file-upload#removeFile"
                    data-index="${index}">
              <span class="w-4 h-4 inline-flex items-center justify-center">
                <svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 6L6 18M6 6L18 18"/>
                </svg>
              </span>
            </button>
          ` : ''}
        </div>
      `
      this.fileListTarget.insertAdjacentHTML('beforeend', chipHtml)
    })
  }

  // Load preloaded persisted files from saved configuration
  loadPreloadedFiles() {
    if (!this.preloadedFilesValue || this.preloadedFilesValue.length === 0) {
      return
    }

    this.preloadedFilesValue.forEach(fileData => {
      this.persistedFiles.push(fileData)
      this.renderPersistedFileChip(fileData)
    })
  }

  // Render a persisted file chip (green - already saved on server)
  renderPersistedFileChip(fileData) {
    const chipHtml = `
      <div class="inline-flex items-center gap-2 px-4 py-2 bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg"
           data-persisted-file-chip
           data-blob-id="${fileData.blob_id}">
        <span class="w-4 h-4 inline-flex items-center justify-center text-success-600">
          <svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 11H8M10 15H8M16 7H8M20 6.8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H15.2C16.8802 2 17.7202 2 18.362 2.32698C18.9265 2.6146 19.3854 3.07354 19.673 3.63803C20 4.27976 20 5.11984 20 6.8Z"/>
          </svg>
        </span>
        <span class="font-normal text-sm leading-5 text-[#1F2937]">${this.escapeHtml(fileData.filename)}</span>
        <span class="font-normal text-xs leading-5 text-[#6B7280]">(${this.formatBytes(fileData.byte_size)})</span>
        ${!this.readOnlyValue ? `
          <button type="button"
                  class="flex items-center justify-center hover:opacity-70 transition-opacity text-gray-500"
                  data-action="click->file-upload#removePersistedFile"
                  data-blob-id="${fileData.blob_id}">
            <span class="w-4 h-4 inline-flex items-center justify-center">
              <svg class="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6L18 18"/>
              </svg>
            </span>
          </button>
        ` : ''}
      </div>
    `
    this.fileListTarget.insertAdjacentHTML('beforeend', chipHtml)
  }

  // Remove a persisted file (server-side deletion)
  async removePersistedFile(event) {
    event.preventDefault()
    const blobId = event.currentTarget.dataset.blobId
    if (!blobId || !this.deleteFileUrlValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.deleteFileUrlValue, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ blob_id: blobId })
      })

      if (response.ok) {
        this.persistedFiles = this.persistedFiles.filter(f => f.blob_id !== blobId)
        const chip = this.fileListTarget.querySelector(`[data-persisted-file-chip][data-blob-id="${blobId}"]`)
        if (chip) chip.remove()
      } else {
        this.showError('Failed to remove file')
      }
    } catch (error) {
      console.error('Failed to remove file:', error)
      this.showError('Failed to remove file')
    }
  }

  // Transition pending files to persisted chips after successful save (no page reload)
  transitionToPersistedFiles(persistedFilesData) {
    // Clear pending files
    this.files = []
    const pendingChips = this.fileListTarget.querySelectorAll('[data-file-chip]')
    pendingChips.forEach(chip => chip.remove())

    // Render as persisted chips
    persistedFilesData.forEach(fileData => {
      this.persistedFiles.push(fileData)
      this.renderPersistedFileChip(fileData)
    })
  }

  // Format bytes to human readable
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return text.replace(/[&<>"']/g, m => map[m])
  }

  // Show error message
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
      return
    }
    // You can customize this to show toast notifications
    console.error(message)
    alert(message)
  }

  // Get files for form submission
  getFiles() {
    return this.files
  }

  // Load preloaded activities from saved configuration
  loadPreloadedActivities() {
    if (!this.preloadedActivitiesValue || this.preloadedActivitiesValue.length === 0) {
      return
    }

    // Use preloaded activity data from backend instead of querying DOM
    this.preloadedActivitiesValue.forEach(activityData => {
      // Add the chip
      this.addActivityChip(activityData)

      // Dispatch event to notify activity-selector about preloaded activity
      window.dispatchEvent(new CustomEvent('file-upload:activityPreloaded', {
        detail: { activity: activityData },
        bubbles: true
      }))
    })
  }
}
